import React, { useState, useEffect, useMemo } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { colors, radius, shadows } from '../../styles/theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const HESAP_LABELS = {
  '100': 'Kasa', 
  '102': 'Bankalar', 
  '103': 'Verilen Çekler',
  '108': 'Diğer Hazır Değerler', 
  '120': 'Alıcılar', 
  '121': 'Alacak Senetleri',
  '150': 'İlk Madde Malzeme', 
  '153': 'Ticari Mallar', 
  '255': 'Demirbaşlar',
  '320': 'Satıcılar', 
  '335': 'Personele Borçlar', 
  '360': 'Ödenecek Vergi',
  '600': 'Yurtiçi Satışlar', 
  '621': 'SMM', 
  '760': 'Paz. Satış Giderleri',
  '770': 'Genel Yönetim Giderleri', 
  '780': 'Finansman Giderleri',
};

const REF_TIPI_RENK = {
  FATURA: { bg: '#fef3c7', color: '#92400e', label: 'Fatura' },
  ODEME: { bg: '#dcfce7', color: '#166534', label: 'Ödeme' },
  GIDER: { bg: '#fee2e2', color: '#991b1b', label: 'Gider' },
  SAS: { bg: '#e0f2fe', color: '#0369a1', label: 'SAS' },
};

const RefBadge = ({ tipi }) => {
  const r = REF_TIPI_RENK[tipi] || { bg: '#f1f5f9', color: '#475569', label: tipi || '-' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: radius.full,
      fontSize: '11px', fontWeight: 600, background: r.bg, color: r.color,
    }}>
      {r.label}
    </span>
  );
};

const formatHesap = (kod) => {
  if (!kod) return '-';
  const label = HESAP_LABELS[kod];
  if (label) return `${kod} - ${label}`;
  // Alt hesap kodları: 320.001 → "320.001 - Satıcılar" (ana hesap label'ı)
  const anaHesap = kod.split('.')[0];
  const anaLabel = HESAP_LABELS[anaHesap];
  if (anaLabel) return `${kod} - ${anaLabel}`;
  return kod;
};

const fmt = (v) => (v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getDefaultDates = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return {
    baslangic: `${y}-${m}-01`,
    bitis: `${y}-${m}-${String(new Date(y, now.getMonth() + 1, 0).getDate()).padStart(2, '0')}`,
  };
};

const MuhasebeFisleriPage = () => {
  const defaults = getDefaultDates();
  const [fisler, setFisler] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baslangic, setBaslangic] = useUrlState('baslangic', defaults.baslangic);
  const [bitis, setBitis] = useUrlState('bitis', defaults.bitis);
  const [refTipi, setRefTipi] = useUrlState('reftipi', '');
  const [firma, setFirma] = useUrlState('firma', '');
  const [hesapKodu, setHesapKodu] = useUrlState('hesap', '', { debounceMs: 300 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (baslangic) params.append('baslangic', baslangic);
      if (bitis) params.append('bitis', bitis);
      if (refTipi) params.append('referans_tipi', refTipi);
      if (firma) params.append('firma', firma);
      if (hesapKodu.trim()) params.append('hesap_kodu', hesapKodu.trim());
      params.append('limit', '200');

      const res = await fetch(`${API_URL}/api/muhasebe/fisler?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setFisler(Array.isArray(data) ? data : []);
      } else {
        setFisler([]);
      }
    } catch {
      setFisler([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/firmalar`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setFirmalar(Array.isArray(data) ? data : []);
        }
      } catch {
        // sessiz geç; hardcoded fallback yok, DB kaynaklı
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ozet = useMemo(() => {
    let toplamBorc = 0;
    let toplamAlacak = 0;
    fisler.forEach(f => {
      toplamBorc += (f.tutar || 0);
      toplamAlacak += (f.tutar || 0);
    });
    return { toplamBorc, toplamAlacak, fisSayisi: fisler.length };
  }, [fisler]);

  const toplamTutar = useMemo(() => {
    return fisler.reduce((acc, f) => acc + (f.tutar || 0), 0);
  }, [fisler]);

  const firmaMap = useMemo(() => {
    const m = {};
    firmalar.forEach(f => { if (f.kod) m[f.kod] = f.ad; });
    return m;
  }, [firmalar]);

  const inputStyle = {
    padding: '8px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
    fontSize: '13px', color: colors.textPrimary, background: colors.surface,
    outline: 'none', transition: 'border-color 0.15s',
  };

  const selectStyle = {
    ...inputStyle, cursor: 'pointer',
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: colors.textPrimary, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em' }}>
        Muhasebe Fisleri
      </h2>

      {/* Filtreler */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end',
        background: colors.surface, borderRadius: radius.lg, padding: '16px',
        boxShadow: shadows.sm, marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Başlangıç</label>
          <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Bitiş</label>
          <input type="date" value={bitis} onChange={e => setBitis(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Referans Tipi</label>
          <select value={refTipi} onChange={e => setRefTipi(e.target.value)} style={selectStyle}>
            <option value="">Tümü</option>
            <option value="FATURA">Fatura</option>
            <option value="ODEME">Ödeme</option>
            <option value="GIDER">Gider</option>
            <option value="SAS">SAS</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Firma</label>
          <select value={firma} onChange={e => setFirma(e.target.value)} style={selectStyle}>
            <option value="">Tümü</option>
            {firmalar.filter(f => f.aktif !== false).map(f => (
              <option key={f.kod} value={f.kod}>
                {f.ad}{f.tip === 'MASRAF_MERKEZI' ? ' (Masraf Merkezi)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Hesap Kodu</label>
          <input type="text" placeholder="Ör: 150" value={hesapKodu} onChange={e => setHesapKodu(e.target.value)} style={{ ...inputStyle, width: '100px' }} />
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: '8px 20px', borderRadius: radius.sm, border: 'none',
            background: colors.primary, color: colors.textOnPrimary,
            fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1, transition: 'opacity 0.15s',
          }}
        >
          {loading ? 'Yükleniyor...' : 'Getir'}
        </button>
      </div>

      {/* Özet Kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Toplam Borç', value: fmt(ozet.toplamBorc), color: colors.danger },
          { label: 'Toplam Alacak', value: fmt(ozet.toplamAlacak), color: colors.success },
          { label: 'Fiş Sayısı', value: ozet.fisSayisi.toLocaleString('tr-TR'), color: colors.info },
        ].map(k => (
          <div
            key={k.label}
            style={{
              background: colors.surface, borderRadius: radius.lg,
              borderLeft: `4px solid ${k.color}`,
              padding: '16px', boxShadow: shadows.sm,
            }}
          >
            <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              {k.label}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
              {k.value} {k.label !== 'Fiş Sayısı' && <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Tablo */}
      <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
              {['Fiş No', 'Tarih', 'Borç Hesap', 'Alacak Hesap', 'Tutar', 'Referans', 'Firma', 'Açıklama'].map(h => (
                <th key={h} style={{
                  padding: '6px 8px', textAlign: 'left', fontWeight: 600,
                  color: colors.textSecondary, fontSize: '11px', textTransform: 'uppercase',
                  letterSpacing: '0.06em', whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>Yükleniyor...</td></tr>
            ) : fisler.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>Kayıt bulunamadı</td></tr>
            ) : (
              fisler.map((f, idx) => (
                <tr key={f.id || idx} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500, color: colors.textPrimary, whiteSpace: 'nowrap' }}>
                    {f.fis_no || '-'}
                  </td>
                  <td style={{ padding: '10px 12px', color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    {f.fis_tarihi || '-'}
                  </td>
                  <td style={{ padding: '10px 12px', color: colors.textPrimary, whiteSpace: 'nowrap' }}>
                    {formatHesap(f.borc_hesap_kodu)}
                  </td>
                  <td style={{ padding: '10px 12px', color: colors.textPrimary, whiteSpace: 'nowrap' }}>
                    {formatHesap(f.alacak_hesap_kodu)}
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: colors.textPrimary, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt(f.tutar)} TL
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <RefBadge tipi={f.referans_tipi} />
                    {f.referans_no && (
                      <span style={{ marginLeft: '6px', fontSize: '11px', color: colors.textTertiary }}>{f.referans_no}</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    {firmaMap[f.firma] || f.firma || '-'}
                  </td>
                  <td style={{ padding: '10px 12px', color: colors.textSecondary, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.aciklama || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {fisler.length > 0 && (
            <tfoot>
              <tr style={{ background: colors.backgroundAlt, borderTop: `2px solid ${colors.border}` }}>
                <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, fontSize: '12px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Toplam ({fisler.length} fiş)
                </td>
                <td style={{ padding: '10px 12px', fontWeight: 700, color: colors.textPrimary, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {fmt(toplamTutar)} TL
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default MuhasebeFisleriPage;
